import { Head, Link, useForm } from '@inertiajs/react'
import { HomeMark } from '../../shared/HomeMark.jsx'
import { withSiteFooter } from '../../shared/SiteFooter.jsx'
import { Button } from '../../components/core/Button.jsx'

// GET /unsubscribe/:token — the footer link in the two trial emails.
//
// It asks before it acts, and that is the point of the page rather than a
// formality: corporate mail scanners fetch every link in a message before a
// person sees it, so a link that unsubscribed on GET would unsubscribe people
// who never asked. A mail client's own one-click button POSTs to the same path
// and never sees this page.
//
// Three states, all decided by the server: a link that resolves to nobody, the
// question, and done. `done` arrives true on a second visit too, so a link
// opened twice reads as finished rather than asking again.
//
// What still arrives is said out loud on purpose. Somebody stopping "emails"
// may reasonably expect the reset link to stop too, and the one moment that
// matters is when they need it.

export default function Unsubscribe({ found, token = '', email = '', done = false }) {
  const form = useForm({})

  function submit(e) {
    e.preventDefault()
    form.post(`/unsubscribe/${token}`)
  }

  if (!found) {
    return (
      <Shell title="This link does not match an account">
        <p className="text-[15px] text-[color:var(--text-body)]">
          Open it straight from the email. A link that has been copied can lose its end on the way.
        </p>
      </Shell>
    )
  }

  if (done) {
    return (
      <Shell title="Emails about your trial are stopped">
        <p className="text-[15px] text-[color:var(--text-body)]">
          For {email}. Address confirmations, password resets and your first result still arrive.
        </p>
        <p className="text-[13.5px] text-[color:var(--text-muted)]">
          {/* No underline class: `base.css` draws links unlayered, which outranks
              a Tailwind utility (see the cascade-layer landmine), and the system
              underlines on hover by design. */}
          <Link href="/">Go to Spruce</Link>
        </p>
      </Shell>
    )
  }

  return (
    <Shell title="Stop emails about your trial">
      <p className="text-[15px] text-[color:var(--text-body)]">
        These are the emails about your trial ending, sent to {email}. Address confirmations, password
        resets and your first result still arrive.
      </p>
      <form onSubmit={submit}>
        <Button variant="primary" size="lg" block as="button" loading={form.processing}>
          Stop these emails
        </Button>
      </form>
    </Shell>
  )
}

// The auth pages' column, so a page reached from an email looks like the reset
// and confirmation pages reached the same way.
function Shell({ title, children }) {
  return (
    <>
      <Head title={title} />
      <main className="mx-auto flex min-h-screen w-full max-w-md flex-col justify-center gap-8 px-6 py-16">
        <HomeMark />
        <div className="flex flex-col gap-4">
          <h1 className="page-title">{title}</h1>
          {children}
        </div>
      </main>
    </>
  )
}

Unsubscribe.layout = withSiteFooter
